import { categories } from "@/mocks/categories";

type FilterSidebarProps = {
  category: string;
  statuses: string[];
  sort: string;
  query: string;
  onCategory: (value: string) => void;
  onToggleStatus: (value: string) => void;
  onSort: (value: string) => void;
  onQuery: (value: string) => void;
  onReset: () => void;
};

const STATUS_OPTIONS = ["追踪中", "已反转", "已回应"];

const SORT_OPTIONS = [
  { value: "latest", label: "按最新更新" },
  { value: "heat", label: "按热度排序" },
  { value: "nodes", label: "按节点数量" },
];

export default function FilterSidebar({
  category,
  statuses,
  sort,
  query,
  onCategory,
  onToggleStatus,
  onSort,
  onQuery,
  onReset,
}: FilterSidebarProps) {
  return (
    <aside className="flex flex-col gap-4 lg:sticky lg:top-28">
      <div className="rounded-lg border border-background-300 bg-background-50 p-4 md:p-5">
        <label
          htmlFor="story-search"
          className="mb-2 block font-label text-xs font-medium uppercase tracking-[0.16em] text-foreground-500"
        >
          关键词搜索
        </label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            id="story-search"
            type="search"
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder="搜标题、标签或摘要"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="rounded-lg border border-background-300 bg-background-50 p-4 md:p-5">
        <h3 className="mb-3 font-label text-xs font-medium uppercase tracking-[0.16em] text-foreground-500">
          追踪分类
        </h3>
        <ul className="flex flex-col gap-1">
          <li>
            <button
              type="button"
              onClick={() => onCategory("全部")}
              className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left font-label text-sm transition-colors ${
                category === "全部"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              全部分类
              <i className="ri-stack-line text-base" />
            </button>
          </li>
          {categories.map((cat) => (
            <li key={cat.id}>
              <button
                type="button"
                onClick={() => onCategory(cat.id)}
                className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left font-label text-sm transition-colors ${
                  category === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {cat.name}
                <span
                  className={`shrink-0 text-xs ${
                    category === cat.id ? "text-background-200" : "text-foreground-400"
                  }`}
                >
                  {cat.count}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border border-background-300 bg-background-50 p-4 md:p-5">
        <h3 className="mb-3 font-label text-xs font-medium uppercase tracking-[0.16em] text-foreground-500">
          事件状态
        </h3>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((status) => {
            const active = statuses.includes(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => onToggleStatus(status)}
                aria-pressed={active}
                className={`rounded-full border px-3 py-1.5 font-label text-xs transition-colors ${
                  active
                    ? "border-primary-400 bg-primary-100 text-primary-800"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:border-foreground-300"
                }`}
              >
                {status}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-background-300 bg-background-50 p-4 md:p-5">
        <h3 className="mb-3 font-label text-xs font-medium uppercase tracking-[0.16em] text-foreground-500">
          排序方式
        </h3>
        <div className="flex flex-col gap-1.5">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSort(option.value)}
              aria-pressed={sort === option.value}
              className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-left font-label text-sm transition-colors ${
                sort === option.value
                  ? "bg-background-200 text-foreground-950"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              <i
                className={
                  sort === option.value
                    ? "ri-checkbox-circle-fill text-primary-600"
                    : "ri-circle-line text-foreground-400"
                }
              />
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-dashed border-foreground-300 py-2.5 font-label text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
      >
        <i className="ri-refresh-line" />
        重置全部筛选
      </button>
    </aside>
  );
}